import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Paper,
  Grid,
  Radio,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  FormControl,
  Select,
  Collapse,
  Tooltip,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ModeStandbyIcon from "@mui/icons-material/ModeStandby";
import CommentIcon from "@mui/icons-material/Comment";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { LoaderWithoutHeight } from "components/Loader";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestEditHeader } from "components/DesignSystem/RequestEditHeader";
import RequestDetailsSection from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { RequestStatusTimeline } from "components/DesignSystem/RequestStatusTimeline";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { useFetch } from "services/hooks/useFetch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ExclamatryiconIcon from "@mui/icons-material/ErrorOutline";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { tooltipStyleProps } from "services/constants/Styles";
import { designColors } from "services/constants/designTokens";
import { normalizeApprovalPersonFields } from "pages/TeamZone/Approve/displayNameUtils";

const SummaryCard = ({ title, amount, icon: Icon, color, lightColor }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, borderRadius: 2, borderColor: color, flex: 1 }}
  >
    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
      <Box
        sx={{
          p: 0.5,
          borderRadius: 1,
          bgcolor: lightColor,
          color: color,
          display: "flex",
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Typography variant="caption" color="text.secondary" fontWeight="bold">
        {title}
      </Typography>
    </Stack>
    <Typography variant="h5" fontWeight="bold">
      {amount}
    </Typography>
  </Paper>
);

const AdvanceSalaryPanel = ({
  open = true,
  onClose,
  reCallApi,
  mode = "create",
  initialData = null,
  onEdit,
  checkAccess,
  requestUserId = null,
}) => {
  const { domain, userDetails, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );

  const isView = mode === "view";
  const isEdit = mode === "edit";
  const isDetailedView =
    isView &&
    (initialData?.status === "Approved" ||
      initialData.status === "Pre-Closed" ||
      initialData.status === "Partially-Paid" ||
      initialData.status === "Closed" ||
      initialData?.status === "Active");

  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const [formData, setFormData] = useState({
    advance_type: "Salary",
    request_date: dayjs().format("YYYY-MM-DD"),
    amount: "",
    reason: "",
    repayment_mode: "Full",
    effective_repayment_date: dayjs()
      .add(1, "month")
      .startOf("month")
      .format("YYYY-MM-DD"),
    no_of_installments: 2,
    repayment_frequency: "Monthly",
  });

  // Repayment Start is presented as a month picker (e.g. "September 2026")
  // rather than a raw day-level date; the underlying value stays the 1st of
  // that month. If an existing record's date (edit mode) doesn't land on the
  // 1st of one of these months, it's appended so the Select never renders an
  // out-of-range value.
  const repaymentMonthOptions = Array.from({ length: 12 }, (_, i) => {
    const m = dayjs().add(i + 1, "month").startOf("month");
    return { value: m.format("YYYY-MM-DD"), label: m.format("MMMM YYYY") };
  });
  if (
    formData.effective_repayment_date &&
    !repaymentMonthOptions.some(
      (opt) => opt.value === formData.effective_repayment_date,
    )
  ) {
    repaymentMonthOptions.unshift({
      value: formData.effective_repayment_date,
      label: dayjs(formData.effective_repayment_date).format("MMMM YYYY"),
    });
  }

  // Skip request states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRepaymentRow, setSelectedRepaymentRow] = useState(null);
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [skipPayload, setSkipPayload] = useState({
    skip_payrun_id: "",
    reason: "",
  });

  const [expandedRow, setExpandedRow] = useState(null);

  // Payruns for skip dialog
  const payrunUrl = topbarCompanyId
    ? `${domain}payroll/operations/payrollprocessing/payrollruncycle/payschedules/?status=Upcoming&company_id=${topbarCompanyId}`
    : "";
  const { result: payruns = [] } = useFetch(payrunUrl);

  // Function to fetch details - abstracted so it can be called after skips
  const fetchDetails = async () => {
    if (!initialData?.id) return;
    setLoadingDetails(true);
    try {
      const res = await customGetAPI(
        `${domain}advance/request/${initialData.id}/?company_id=${topbarCompanyId}`,
      );
      // Extract the nested 'data' object reliably
      const extractedData = res.responseDetails || res.data || res;
      setDetailedData(extractedData);
    } catch (err) {
      console.error("Failed to fetch advance details", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (isDetailedView) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetailedView, initialData?.id, domain]);

  useEffect(() => {
    if ((isView || isEdit) && initialData) {
      const repaymentDate =
        initialData.repayment_date || initialData.repayment_start_date || "";
      // Ensure date is in YYYY-MM-DD format for date input
      const formattedDate = repaymentDate
        ? dayjs(repaymentDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : "";
      const formattedRequestDate = initialData.request_date
        ? dayjs(initialData.request_date, "DD-MM-YYYY").format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");

      setFormData({
        advance_type: "Salary",
        request_date: formattedRequestDate,
        amount: initialData.amount || "",
        reason: initialData.description || initialData.reason || "",
        repayment_mode: initialData.repayment_mode || "Full",
        effective_repayment_date: formattedDate,
        no_of_installments:
          initialData.no_of_installments ||
          initialData.number_of_installments ||
          2,
        repayment_frequency: initialData.repayment_frequency || "Monthly",
      });
    }
  }, [isView, isEdit, initialData]);

  // Handle Double nested 'data' scenarios if API response dictates
  const rawDisplayData = detailedData?.data || detailedData || initialData || {};
  // Strips a trailing employee-id suffix off raw actor names (e.g.
  // "Cauver21249" -> "Cauver") the same way MyZone's Leave view already
  // does, in case any actor field still carries one through.
  const displayData = normalizeApprovalPersonFields(rawDisplayData);
  // Closed/Pre-Closed (or every instalment paid) means the advance is done.
  // Active/Partially-Paid are still being repaid. The timeline only knows the
  // Approved/Completed flows, so the raw status must be mapped onto them.
  const advanceStatusKey = String(displayData?.status || "").trim().toLowerCase();
  const isAdvanceCompleted =
    advanceStatusKey === "closed" ||
    advanceStatusKey === "pre-closed" ||
    (isDetailedView &&
      Array.isArray(displayData?.repayment_schedule) &&
      displayData.repayment_schedule.length > 0 &&
      displayData.repayment_schedule.every((row) => row.status === "Paid"));
  const statusFlowData = {
    ...displayData,
    status: ["active", "partially-paid", "pre-closed", "closed"].includes(
      advanceStatusKey,
    )
      ? "Approved"
      : displayData?.status,
    applied_by: displayData?.applied_by || displayData?.user_name || userDetails?.first_name,
    applied_on:
      displayData?.applied_on ||
      displayData?.request_date ||
      displayData?.created_at ||
      displayData?.created_date,
    // displayData?.approver_name is the assigned approver, stamped at apply
    // time before anyone has acted -- it is not proof of approval (or
    // rejection). The backend's approved_by already recovers the real
    // approver from the audit log for a since-cancelled advance (see
    // test_advance_cancel_display_fields.py) -- approver_name here would
    // silently undo that fix.
    approved_by: displayData?.approved_by,
    approved_at: displayData?.approved_at || displayData?.approved_on,
    rejected_by: displayData?.rejected_by,
    rejected_at: displayData?.rejected_at,
    // The backend's field is rejection_reason, not rejected_reason -- this
    // was reading the wrong key entirely (always undefined), which is why
    // a rejected advance's reason never showed here. Falling back to the
    // plain request reason would also mislabel the employee's own original
    // request reason as the manager's reject reason, so that fallback is
    // dropped too, not just the key name fixed.
    rejected_reason: displayData?.rejection_reason,
    // cancellation_reason is a genuinely separate column from
    // rejection_reason (AdvanceService.cancel) but wasn't previously
    // exposed by the serializer at all, so a cancelled advance never
    // showed why -- now that the backend returns it, surface it under the
    // key RequestStatusTimeline's Cancelled flow reads.
    cancelled_reason: displayData?.cancellation_reason,
  };

  const isInstallment = formData.repayment_mode?.startsWith("Installment");

  const repaymentAmount =
    isInstallment && formData.amount && formData.no_of_installments > 0
      ? (
          parseFloat(formData.amount) / parseInt(formData.no_of_installments)
        ).toFixed(2)
      : 0;

  const repaymentAmountLabel = `${
    formData.repayment_frequency.charAt(0).toUpperCase() +
    formData.repayment_frequency.slice(1)
  } Repayment Amount`;

  const handleChange = (field, value) => {
    // Validate date fields - prevent past dates
    // if (field === "effective_repayment_date" && value) {
    //   const selectedDate = dayjs(value, "YYYY-MM-DD");
    //   const today = dayjs().startOf("day");
    //   if (selectedDate.isBefore(today)) {
    //     showToast("error", "Please select a future date for repayment.");
    //     return;
    //   }
    // }

    setFormData((prev) => {
      const newState = { ...prev, [field]: value };
      if (field === "repayment_mode") {
        if (value === "Full") {
          newState.no_of_installments = 1;
        } else if (value === "Installment") {
          newState.no_of_installments = Math.max(
            2,
            parseInt(prev.no_of_installments, 10) || 2,
          );
        }
      }
      if (field === "no_of_installments") {
        const intVal = parseInt(value, 10);
        newState.no_of_installments = Number.isNaN(intVal)
          ? ""
          : Math.max(2, intVal);
      }
      return newState;
    });
  };

  const isSubmitDisabled = () => {
    if (!formData.request_date) return true;
    if (!formData.amount || parseFloat(formData.amount) <= 0) return true;
    if (!formData.reason?.trim()) return true;

    if (!formData.effective_repayment_date) return true;
    if (
      isInstallment &&
      (!formData.no_of_installments || formData.no_of_installments < 2)
    )
      return true;
    return false;
  };

  const handleAmountChange = (value) => {
    if (!value) {
      handleChange("amount", "");
      return;
    }

    // Remove non-digits (extra safety)
    let sanitized = value.replace(/\D/g, "");

    // Remove leading zeros
    sanitized = sanitized.replace(/^0+/, "");

    // Limit to 8 digits
    sanitized = sanitized.slice(0, 8);

    handleChange("amount", sanitized);
  };
  const handleAmountPaste = (e) => {
    e.preventDefault();

    const pastedData = e.clipboardData.getData("text");

    let sanitized = pastedData.replace(/\D/g, "");
    sanitized = sanitized.replace(/^0+/, "");
    sanitized = sanitized.slice(0, 8);

    handleChange("amount", sanitized);
  };

  const handleSubmit = async () => {
    setActionLoading(true);

    const payload = {
      advance_type: "Salary",
      amount: parseFloat(formData.amount),
      reason: formData.reason,
      repayment_mode: formData.repayment_mode,
      repayment_date: formData.effective_repayment_date,
      no_of_installments: isInstallment
        ? parseInt(formData.no_of_installments)
        : 1,
      repayment_frequency: formData.repayment_frequency,
      user_id: requestUserId || userDetails?.id,
    };

    // request_date is fixed at creation (the backend never updates it on
    // edit) — only send it when creating, so editing an older pending
    // request doesn't resend its now-past original date and trip the
    // "cannot be in the past" validation for no reason.
    if (!isEdit) {
      payload.request_date = formData.request_date;
    }

    try {
      const endpoint = isEdit
        ? `${domain}advance/request/${initialData.id}/?company_id=${topbarCompanyId}`
        : `${domain}advance/request/?company_id=${topbarCompanyId}`;
      const method = isEdit ? "PUT" : "POST";

      const res = await customAPI(endpoint, method, payload);

      if (res.ok) {
        showToast(
          "success",
          `Advance request ${isEdit ? "updated" : "submitted"} successfully`,
        );
        if (reCallApi) reCallApi(true);
        if (onClose) onClose();
      } else {
        showToast("error", res.message || "Failed to submit request");
      }
    } catch (error) {
      console.error("Advance Submit Error:", error);
      showToast("error", "Something went wrong.");
    } finally {
      setActionLoading(false);
    }
  };

  // Skip request handlers
  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRepaymentRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openSkipModal = () => {
    setSkipPayload({ skip_payrun_id: "", reason: "" });
    setSkipModalOpen(true);
    handleMenuClose();
  };

  const handleSkipDialogClose = (event, reason) => {
    if (reason === "backdropClick") return;
    setSkipModalOpen(false);
  };

  const handleSkipSubmit = async () => {
    if (!skipPayload.skip_payrun_id || !skipPayload.reason) {
      showToast("error", "Please provide a reason and select a payrun.");
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        user_id: userDetails.id,
        skip_payrun_id: skipPayload.skip_payrun_id,
        reason: skipPayload.reason,
      };

      const res = await customAPI(
        `${domain}advance/repayment/${selectedRepaymentRow.id}/skip/?company_id=${topbarCompanyId}`,
        "POST",
        payload,
      );

      if (res.ok) {
        showToast("success", "Skip requested.");
        setSkipModalOpen(false);
        fetchDetails(); // Refresh details to update the table status
      } else {
        showToast("error", res.message || "Failed to submit skip request.");
      }
    } catch (error) {
      console.error('[Advancesalaryformpanel] operation failed:', error);
      showToast("error", "Error requesting skip.");
    } finally {
      setActionLoading(false);
    }
  };

  const renderDetailedView = () => (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Box
          sx={{
            border: "1px solid #00ab66",
            color: "#00ab66",
            borderRadius: 4,
            px: 2,
            py: 0.5,
            fontSize: "0.8rem",
            fontWeight: "bold",
          }}
        >
          {displayData.state}
        </Box>
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
        <SummaryCard
          title="Advance Amount"
          amount={displayData.approved_amount || displayData.amount || "-"}
          icon={ShoppingBagOutlinedIcon}
          color="#2ecc71"
          lightColor="#eafaf1"
        />
        <SummaryCard
          title="Paid Amount"
          amount={displayData.paid_amount || "0.00"}
          icon={AccountBalanceWalletOutlinedIcon}
          color="#e91e63"
          lightColor="#fce4ec"
        />
        <SummaryCard
          title="Balance"
          amount={displayData.balance_amount || "-"}
          icon={CardGiftcardOutlinedIcon}
          color="#3f51b5"
          lightColor="#e8eaf6"
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={2}>
          {[
            { label: "Request date", value: displayData.request_date },
            { label: "Amount", value: displayData.effective_amount },
            { label: "Reason", value: displayData.reason },
            {
              label: "Disbursement date",
              value: displayData.disbursement_date || "-",
            },
            { label: "Repayment Mode", value: displayData.repayment_mode },
            {
              label: "No of Installment",
              value:
                displayData.approved_installments ||
                displayData.no_of_installments,
            },
          ].map((item, i) => (
            <React.Fragment key={i}>
              <Grid item xs={5}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {item.label}
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography variant="body2" color="text.secondary">
                  {item.value || "-"}
                </Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Paper>

      <Typography variant="subtitle1" fontWeight="bold" mb={2}>
        Repayment History
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, overflowX: "auto" }}
      >
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Due Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Due</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Paid Amount</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Balance</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Payrun</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Maps successfully now since we properly extracted inner data */}
            {displayData.repayment_schedule?.length > 0 ? (
              displayData.repayment_schedule.map((row, index) => (
                <React.Fragment key={index}>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: "nowrap", width: "80px" }}>
                      {row.due_date || "-"}
                    </TableCell>
                    <TableCell>{row.amount_due || "-"}</TableCell>
                    <TableCell>{row.amount_paid || "-"}</TableCell>
                    <TableCell>{row.balance || "-"}</TableCell>

                    <TableCell>
                      <Tooltip
                        title={row.payrun_label || "N/A"}
                        slotProps={tooltipStyleProps}
                      >
                        {row.payrun || "N/A"}
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {row.skip_status !== null &&
                      row.skip_status !== undefined ? (
                        <Box
                          sx={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                          onClick={() => {
                            setExpandedRow((prev) =>
                              prev === index ? null : index,
                            );
                          }}
                        >
                          {row.status || "-"}
                          <InfoOutlinedIcon
                            sx={{ fontSize: 14, opacity: 0.7 }}
                          />
                        </Box>
                      ) : (
                        row.status || "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, row)}
                        disabled={
                          row.status === "Skipped" ||
                          row.status === "Paid" ||
                          row.status === "Skip-Requested"
                        }
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {row.skip_status !== null &&
                    row.skip_status !== undefined && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{ p: 0, bgcolor: "action.hover" }}
                        >
                          <Collapse
                            in={expandedRow === index}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ p: 2, bgcolor: "#fafbfc" }}>
                              <Paper
                                elevation={0}
                                sx={{
                                  position: "relative",
                                  borderRadius: 2,
                                  border: "1px solid #eaebec",
                                  overflow: "hidden",
                                  bgcolor: "#fff",
                                }}
                              >
                                {/* Yellow Left Accent Border */}
                                <Box
                                  sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    bgcolor: "#f5a623",
                                  }}
                                />

                                {/* Header */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 2,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <PaymentsOutlinedIcon
                                      sx={{ fontSize: 20, color: "#f5a623" }}
                                    />
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={700}
                                      sx={{
                                        borderBottom: "1px solid #ccc",
                                        pb: 0.2,
                                      }}
                                    >
                                      Skip Details
                                    </Typography>
                                  </Stack>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setExpandedRow(null)}
                                    sx={{
                                      color: "#333",
                                      borderColor: "#d3d3d3",
                                      textTransform: "none",
                                      borderRadius: 2,
                                    }}
                                  >
                                    Hide
                                  </Button>
                                </Box>

                                {/* Detail Cards */}
                                <Grid
                                  container
                                  spacing={2}
                                  sx={{ px: 2, alignItems: "stretch" }}
                                >
                                  {/* Status Card */}
                                  <Grid item xs={12} md={4}>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        height: "100%",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          bgcolor: "#e6f4ea",
                                          borderRadius: "50%",
                                          p: 0.8,
                                          display: "flex",
                                        }}
                                      >
                                        {row.skip_status?.toLowerCase() ===
                                        "approved" ? (
                                          <CheckCircleIcon
                                            sx={{
                                              color: "#34a853",
                                              fontSize: 20,
                                            }}
                                          />
                                        ) : row.skip_status?.toLowerCase() ===
                                          "rejected" ? (
                                          <CancelIcon
                                            sx={{
                                              color: "#f44336",
                                              fontSize: 20,
                                            }}
                                          />
                                        ) : (
                                          <ExclamatryiconIcon
                                            sx={{
                                              color: "#131312",
                                              fontSize: 20,
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          Skip Status
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              bgcolor:
                                                row.skip_status?.toLowerCase() ===
                                                "approved"
                                                  ? "#e6f4ea"
                                                  : row.skip_status?.toLowerCase() ===
                                                      "rejected"
                                                    ? "#fdecea"
                                                    : "#f3ded8",
                                              color:
                                                row.skip_status?.toLowerCase() ===
                                                "approved"
                                                  ? "#137333"
                                                  : row.skip_status?.toLowerCase() ===
                                                      "rejected"
                                                    ? "#f84913"
                                                    : "#131312",
                                              px: 1,
                                              py: 0.5,
                                              borderRadius: 1,
                                              fontWeight: 600,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            {row.skip_status?.toLowerCase() ===
                                              "approved" && (
                                              <CheckCircleIcon
                                                sx={{ fontSize: 14 }}
                                              />
                                            )}
                                            {row.skip_status?.toLowerCase() ===
                                              "rejected" && (
                                              <CancelIcon
                                                sx={{ fontSize: 14 }}
                                              />
                                            )}
                                            {row.skip_status?.toLowerCase() ===
                                              "pending" && (
                                              <ExclamatryiconIcon
                                                sx={{ fontSize: 14 }}
                                              />
                                            )}
                                            {row.skip_status || "Pending"}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Paper>
                                  </Grid>

                                  {/* Reason Card */}
                                  <Grid item xs={12} md={4}>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 2,
                                        height: "100%",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          bgcolor: "#e8f0fe",
                                          borderRadius: "50%",
                                          minWidth: 34,
                                          height: 34,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <CommentIcon
                                          sx={{ fontSize: 16, color: "#2878ff" }}
                                        />
                                      </Box>
                                      <Box sx={{ width: "100%" }}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          Skip Reason
                                        </Typography>
                                        <Tooltip
                                          title={row.skip_reason || "N/A"}
                                          slotProps={tooltipStyleProps}
                                          arrow
                                        >
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{
                                              mt: 0.5,
                                              display: "-webkit-box",
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            {row.skip_reason || "N/A"}
                                          </Typography>
                                        </Tooltip>
                                      </Box>
                                    </Paper>
                                  </Grid>

                                  {/* Remark Card */}
                                  <Grid item xs={12} md={4}>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 2,
                                        height: "100%",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          bgcolor: "#f1f3f4",
                                          borderRadius: "50%",
                                          minWidth: 34,
                                          height: 34,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <CommentIcon
                                          sx={{ fontSize: 16, color: "#5f6368" }}
                                        />
                                      </Box>
                                      <Box sx={{ width: "100%" }}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          Skip Remark
                                        </Typography>
                                        <Tooltip
                                          title={
                                            row.skip_review_remarks || "N/A"
                                          }
                                          slotProps={tooltipStyleProps}
                                          arrow
                                        >
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{
                                              mt: 0.5,
                                              display: "-webkit-box",
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            {row.skip_review_remarks || "N/A"}
                                          </Typography>
                                        </Tooltip>
                                      </Box>
                                    </Paper>
                                  </Grid>
                                </Grid>

                                {/* Footer Details */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    p: 2,
                                    mt: 1,
                                    gap: { xs: 2, md: 4 },
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                  >
                                    <CalendarTodayIcon
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: 20,
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        fontWeight={600}
                                      >
                                        Requested On
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color="#333"
                                      >
                                        {row.skip_requested_on || "-"}
                                      </Typography>
                                    </Box>
                                  </Stack>

                                  <Box
                                    sx={{
                                      width: "1px",
                                      height: "30px",
                                      bgcolor: "#e0e0e0",
                                    }}
                                  />

                                  <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                  >
                                    <PersonOutlineOutlinedIcon
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: 22,
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        fontWeight={600}
                                      >
                                        {row.skip_status === "Approved" ||
                                        row.skip_status === "Pending"
                                          ? "Approved By"
                                          : "Rejected By"}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color="#333"
                                      >
                                        {row.skip_approved_by || "-"}
                                      </Typography>
                                    </Box>
                                  </Stack>

                                  <Box
                                    sx={{
                                      width: "1px",
                                      height: "30px",
                                      bgcolor: "#e0e0e0",
                                      display: { xs: "none", md: "block" },
                                    }}
                                  />

                                  <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                  >
                                    <EventAvailableOutlinedIcon
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: 22,
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        fontWeight={600}
                                      >
                                        {row.skip_status === "Approved" ||
                                        row.skip_status === "Pending"
                                          ? "Approved On"
                                          : "Rejected On"}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color="#333"
                                      >
                                        {row.skip_approved_on || "-"}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Box>
                              </Paper>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  No repayment schedule available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu for the Repayment Table */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ elevation: 2, sx: { minWidth: 120 } }}
      >
        {selectedRepaymentRow?.status === "Pending" && (
          <MenuItem onClick={openSkipModal} sx={{ fontSize: "0.9rem" }}>
            Skip Request
          </MenuItem>
        )}
      </Menu>
    </Box>
  );

  // Skip modal
  const renderSkipModal = () => (
    <Dialog
      open={skipModalOpen}
      onClose={handleSkipDialogClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Skip Advance Repayment
        </Typography>
        <IconButton onClick={() => setSkipModalOpen(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderBottom: "none" }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Skip Payrun
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={skipPayload.skip_payrun_id}
              onChange={(e) =>
                setSkipPayload({
                  ...skipPayload,
                  skip_payrun_id: e.target.value,
                })
              }
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Payrun
              </MenuItem>
              {payruns.map((payrun) => (
                <MenuItem key={payrun.id} value={payrun.id}>
                  {payrun.name || payrun.label || `Payrun ${payrun.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Reason
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter Reason"
            value={skipPayload.reason}
            onChange={(e) =>
              setSkipPayload({ ...skipPayload, reason: e.target.value })
            }
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Button
          onClick={() => setSkipModalOpen(false)}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSkipSubmit}
          variant="contained"
          disabled={actionLoading}
          sx={{ bgcolor: "#84DEB8", "&:hover": { bgcolor: "#008f55" } }}
        >
          {actionLoading ? "Processing..." : "Skip"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderCancelDialog = () => (
    <Dialog
      open={showCancelDialog}
      onClose={() => setShowCancelDialog(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Cancel Salary Advance Request
        </Typography>
        <IconButton onClick={() => setShowCancelDialog(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderBottom: "none" }}>
        <Typography sx={{ mb: 2, color: "text.secondary" }}>
          Are you sure you want to cancel this request? Please provide a reason.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Enter reason for cancellation"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Button
          onClick={() => setShowCancelDialog(false)}
          variant="outlined"
          color="inherit"
        >
          Close
        </Button>
        <Button
          onClick={handleCancelRequest}
          variant="contained"
          color="error"
          disabled={cancelling}
        >
          {cancelling ? "Cancelling..." : "Confirm Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Edit/Cancel used to be gated on status alone, so a view-only user could
  // open either from this panel with no permission check at all (the New
  // Request button on AdvanceSalary/index.js already gates creation on this
  // same checkAccess). Both now require it too, hiding rather than
  // disabling the action for a viewer who can't use it.
  const editAction =
    isView &&
    checkAccess &&
    onEdit &&
    !isDetailedView &&
    initialData?.status !== "Rejected" ? (
      <IconButton onClick={onEdit}>
        <EditIcon />
      </IconButton>
    ) : null;

  const canCancel = isView && checkAccess && initialData?.status === "Pending";

  const handleCancelRequest = async () => {
    if (cancelReason.trim().length < 3) {
      showToast("error", "Please provide a reason (minimum 3 characters).");
      return;
    }
    setCancelling(true);
    const response = await customAPI(
      `${domain}advance/request/${initialData?.id}/cancel/&company_id=${topbarCompanyId}`,
      "PUT",
      { reason: cancelReason.trim() },
    );
    setCancelling(false);
    if (response?.ok) {
      showToast(
        "success",
        response?.responseDetails?.message || "Salary advance request cancelled",
      );
      setShowCancelDialog(false);
      if (reCallApi) reCallApi(true);
      onClose();
    } else {
      showToast("error", response?.message);
    }
  };

  const footerContent = !isView ? (
    <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        spacing={1.5}
        justifyContent="flex-end"
        sx={{ width: "100%" }}
      >
        <SecondaryButton onClick={onClose} sx={{ width: { xs: "100%", sm: "auto" } }}>
          {isEdit ? "Discard" : "Cancel"}
        </SecondaryButton>
        <PrimaryButton
          onClick={handleSubmit}
          disabled={isSubmitDisabled()}
          loading={actionLoading}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {isEdit ? "Update" : "Submit Request"}
        </PrimaryButton>
      </Stack>
    </Box>
  ) : canCancel ? (
    <RequestCancelButton onClick={() => setShowCancelDialog(true)} />
  ) : null;

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      header={
        isEdit ? <RequestEditHeader title="Salary Advance Request" /> : undefined
      }
      title={
        isEdit
          ? undefined
          : isView
          ? isDetailedView
            ? "Salary Advance"
            : "View Salary Advance Request"
            : "Salary Advance Request"
      }
      subtitle={
        isEdit
          ? undefined
          : isView
            ? "View your salary advance request."
            : "Submit a salary advance request."
      }
      headerActions={editAction}
      footer={footerContent}
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <RequestDetailHeader
            variant="employeeDetailsCard"
            userDetails={{
              image_url: userDetails?.image_url,
              employee_id: displayData?.employee_id || userDetails?.employee_id,
              user_name: userDetails?.first_name,
              department_name:
                displayData?.department || userDetails?.department_name,
              designation: displayData?.designation || userDetails?.designation,
            }}
            status={isView ? displayData?.status : undefined}
          />
        </Box>

        {isView ? (
          loadingDetails ? (
            <LoaderWithoutHeight info="Loading details..." />
          ) : isDetailedView ? (
            renderDetailedView()
          ) : (
            <Stack spacing={2}>
              <RequestDetailsSection
                fields={[
                  {
                    icon: AccountBalanceWalletOutlinedIcon,
                    label: "Amount",
                    value: formData.amount || "-",
                    accent: detailAccentPalette.indigo,
                  },
                        {
                          icon: ModeStandbyIcon,
                          label: "Repayment Mode",
                          value:
                            formData.repayment_mode === "Full"
                              ? "Full Deduction"
                              : "Installments",
                          accent: detailAccentPalette.pink,
                        },
                        ...(formData.repayment_mode === "Full"
                          ? [
                              {
                                icon: CalendarTodayIcon,
                                label: "Repayment Date",
                                value: formData.effective_repayment_date
                                  ? dayjs(formData.effective_repayment_date).format("DD MMM YYYY")
                                  : "-",
                                accent: detailAccentPalette.green,
                              },
                            ]
                          : [
                              {
                                icon: HelpOutlineIcon,
                                label: "No. of Installments",
                                value: formData.no_of_installments || "-",
                                accent: detailAccentPalette.purple,
                              },
                              {
                                icon: AccountBalanceWalletIcon,
                                label: repaymentAmountLabel,
                                value: repaymentAmount || "-",
                                accent: detailAccentPalette.blue,
                              },
                              {
                                icon: CalendarTodayIcon,
                                label: "Repayment Start Date",
                                value: formData.effective_repayment_date
                                  ? dayjs(formData.effective_repayment_date).format("DD MMM YYYY")
                                  : "-",
                                accent: detailAccentPalette.green,
                              },
                            ]),
                  {
                    icon: CommentIcon,
                    label: "Reason",
                    value: formData.reason || "-",
                    accent: detailAccentPalette.orange,
                    multiline: true,
                  },
                ]}
              />

              <RequestStatusTimeline
                data={statusFlowData}
                forceCompleted={isAdvanceCompleted}
                hidePendingStep
              />

            </Stack>
          )
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Request Date
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }} alignItems="stretch">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={formData.request_date}
                    onChange={(e) => handleChange("request_date", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <CalendarTodayIcon
                          sx={{ fontSize: 16, color: designColors.slate500, mr: 1 }}
                        />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    sx={{
                      height: "100%",
                      boxSizing: "border-box",
                      p: 1.2,
                      bgcolor: designColors.tealTint,
                      borderRadius: 2,
                    }}
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 18, color: designColors.tealDark, mt: 0.1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      This date is used for approval and{" "}
                      payroll processing.
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Amount
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Enter the advance amount you wish to request.
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Enter advance amount"
                value={formData.amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                onPaste={(e) => handleAmountPaste(e)}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Reason
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Mention the reason for requesting the advance. This will help in
                faster approval.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                placeholder="Enter reason for this advance request"
                value={formData.reason}
                onChange={(e) => {
                  if (e.target.value.length <= 250) {
                    handleChange("reason", e.target.value);
                  }
                }}
                inputProps={{ maxLength: 250 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "right", mt: 0.3 }}
              >
                {formData.reason?.length || 0}/250
              </Typography>
            </Box>

            <>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Repayment Option
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Choose the repayment option for the advance.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() => handleChange("repayment_mode", "Full")}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      border:
                        formData.repayment_mode === "Full"
                          ? `1.5px solid ${designColors.teal}`
                          : `1px solid ${designColors.slate200}`,
                      bgcolor:
                        formData.repayment_mode === "Full"
                          ? designColors.tealTint
                          : "transparent",
                    }}
                  >
                    <Radio
                      checked={formData.repayment_mode === "Full"}
                      size="small"
                      sx={{ p: 0, mt: 0.3, color: designColors.teal }}
                    />
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: designColors.tealTint,
                        color: designColors.tealDark,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PaymentsOutlinedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Full Deduction
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Deduct the full amount from one payroll.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() =>
                      handleChange("repayment_mode", "Installment")
                    }
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      border:
                        formData.repayment_mode === "Installment"
                          ? `1.5px solid ${designColors.teal}`
                          : `1px solid ${designColors.slate200}`,
                      bgcolor:
                        formData.repayment_mode === "Installment"
                          ? designColors.tealTint
                          : "transparent",
                    }}
                  >
                    <Radio
                      checked={formData.repayment_mode === "Installment"}
                      size="small"
                      sx={{ p: 0, mt: 0.3, color: designColors.teal }}
                    />
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: designColors.tealTint,
                        color: designColors.tealDark,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <EventRepeatOutlinedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Installments
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Spread the deduction across multiple payrolls.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {isInstallment && (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Repayment Frequency
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Choose how often the repayments will be deducted.
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <Select
                      value={formData.repayment_frequency}
                      onChange={(e) =>
                        handleChange("repayment_frequency", e.target.value)
                      }
                    >
                      <MenuItem value="Monthly">Monthly</MenuItem>
                      <MenuItem value="Weekly">Weekly</MenuItem>
                      <MenuItem value="Bi-weekly">Bi-weekly</MenuItem>
                      <MenuItem value="Semi-monthly">Semi-monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        No. of Installments
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ minHeight: 40, mb: 1 }}
                      >
                        Total number of installments for repayment.
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="Enter number of installments"
                        value={formData.no_of_installments}
                        onChange={(e) =>
                          handleChange("no_of_installments", e.target.value)
                        }
                        inputProps={{ min: 2 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {repaymentAmountLabel}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ minHeight: 40, mb: 1 }}
                      >
                        Amount to be deducted from each scheduled payroll period.
                      </Typography>
                      <Box
                        sx={{
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ color: designColors.tealDark, fontWeight: "bold" }}
                        >
                          {repaymentAmount}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                Repayment Start
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={
                    formData.effective_repayment_date ||
                    repaymentMonthOptions[0].value
                  }
                  onChange={(e) =>
                    handleChange("effective_repayment_date", e.target.value)
                  }
                  IconComponent={ExpandMoreIcon}
                >
                  {repaymentMonthOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                display: "flex",
                gap: 1,
                bgcolor: designColors.tealTint,
                border: `1px solid ${designColors.teal}`,
              }}
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 18, color: designColors.tealDark, mt: 0.1, flexShrink: 0 }}
              />
              <Typography variant="caption" color="text.secondary">
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: designColors.slate800 }}
                >
                  Note:{" "}
                </Box>
                The advance amount will be recovered through payroll as per
                the selected option.
              </Typography>
            </Box>
            </>
          </Stack>
        )}
      </Box>
      {renderSkipModal()}
      {renderCancelDialog()}
    </GlobalSidePanel>
  );
};

export default AdvanceSalaryPanel;
